import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Mail, Link2 } from 'lucide-react'
import { apis } from '@/modules'
import { useTar } from '@/modules'
import { Button, Input, Textarea, ToggleGroup, ToggleGroupItem } from '@duck/ui'

const MAX_MESSAGE = 500
const EMAIL_REGEX = /\S+@\S+\.\S+/

export const InviteAFriendForm = () => {
  const { t } = useTar()
  const queryClient = useQueryClient()

  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [message, setMessage] = useState('')
  const [mode, setMode] = useState<'email' | 'link'>('email')
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null)
  const [emailFieldError, setEmailFieldError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const response = await apis.userService.createReferral({
        email,
        firstName,
        lastName,
        message: message.trim() ? message.trim() : undefined,
        sendEmail: mode === 'email',
      })
      return response.data
    },
    onSuccess: (data) => {
      setEmailFieldError(null)
      void queryClient.invalidateQueries({ queryKey: ['myInvitations'] })

      if (data.wasExisting) {
        toast(t('refer.form.alreadyInvited'))
        if (mode === 'link' && data.inviteUrl) setGeneratedUrl(data.inviteUrl)
        return
      }
      if (mode === 'email') {
        toast(t('refer.form.successEmail', { email }))
        resetForm()
      } else if (data.inviteUrl) {
        setGeneratedUrl(data.inviteUrl)
        resetForm({ keepMode: true })
      }
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { errorCode?: string } } })?.response?.data?.errorCode
      if (code === 'userAlreadyExists') {
        setEmailFieldError(t('refer.form.userAlreadyExists'))
      } else {
        toast(t('refer.form.genericError'))
      }
    },
  })

  const resetForm = (opts: { keepMode?: boolean } = {}) => {
    setEmail('')
    setFirstName('')
    setLastName('')
    setMessage('')
    if (!opts.keepMode) setMode('email')
  }

  const isEmailValid = EMAIL_REGEX.test(email)
  const isValid = isEmailValid && firstName.length > 0 && lastName.length > 0 && message.length <= MAX_MESSAGE

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (isValid) mutation.mutate()
      }}
    >
      {/* Email */}
      <div className="space-y-1">
        <label htmlFor="refer-email" className="block text-sm font-medium text-foreground">
          {t('refer.form.email')}
          <span className="text-error ms-1">*</span>
        </label>
        <Input
          id="refer-email"
          type="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
            setEmailFieldError(null)
          }}
          onBlur={() => {
            if (email.length > 0 && !EMAIL_REGEX.test(email)) {
              setEmailFieldError(t('refer.form.invalidEmail'))
            }
          }}
          className={`text-foreground ${emailFieldError ? 'border-error focus:ring-error' : ''}`}
        />
        {emailFieldError && (
          <p className="text-sm text-error">{emailFieldError}</p>
        )}
      </div>

      {/* First / Last name */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label htmlFor="refer-first" className="block text-sm font-medium text-foreground">
            {t('refer.form.firstName')}
            <span className="text-error ms-1">*</span>
          </label>
          <Input
            id="refer-first"
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="text-foreground"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="refer-last" className="block text-sm font-medium text-foreground">
            {t('refer.form.lastName')}
            <span className="text-error ms-1">*</span>
          </label>
          <Input
            id="refer-last"
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="text-foreground"
          />
        </div>
      </div>

      {/* Message */}
      <div className="space-y-1">
        <label htmlFor="refer-message" className="block text-sm font-medium text-foreground">
          {t('refer.form.message')}
        </label>
        <Textarea
          id="refer-message"
          maxLength={MAX_MESSAGE}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="min-h-[100px] resize-y text-foreground"
        />
        <p className="text-xs text-muted-foreground">
          {t('refer.form.messageHint', { count: message.length })}
        </p>
      </div>

      {/* Mode toggle */}
      <div className="space-y-1">
        <ToggleGroup
          type="single"
          value={mode}
          onValueChange={(v) => {
            if (v === 'email' || v === 'link') setMode(v)
          }}
          className="inline-flex rounded-md border border-border p-0.5"
        >
          <ToggleGroupItem
            value="email"
            data-testid="refer-mode-email"
            className="gap-2 px-3 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground"
          >
            <Mail className="w-4 h-4" />
            {t('refer.form.modeEmail')}
          </ToggleGroupItem>
          <ToggleGroupItem
            value="link"
            data-testid="refer-mode-link"
            className="gap-2 px-3 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground"
          >
            <Link2 className="w-4 h-4" />
            {t('refer.form.modeLink')}
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {/* Generated URL display */}
      {generatedUrl && (
        <div className="border border-border rounded-md p-3 space-y-2">
          <p className="text-sm text-foreground">{t('refer.form.successLink')}</p>
          <div className="flex items-center gap-2">
            <code
              className="flex-1 break-all text-xs text-foreground bg-muted rounded px-2 py-1"
              dir="ltr"
              data-testid="refer-invite-url"
            >
              {generatedUrl}
            </code>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => copyToClipboard(generatedUrl)}
            >
              {copied ? t('refer.form.copied') : t('refer.form.copyUrl')}
            </Button>
          </div>
        </div>
      )}

      {/* Submit */}
      <Button type="submit" disabled={!isValid || mutation.isPending} data-testid="refer-submit">
        {mutation.isPending ? t('refer.form.submitting') : t('refer.form.submit')}
      </Button>
    </form>
  )
}
